"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddressRepository = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const address_entity_1 = require("../../../../service-lib/src/lib/entities/address.entity");
const region_entity_1 = require("../../../../service-lib/src/lib/entities/region.entity");
const country_entity_1 = require("../../../../service-lib/src/lib/entities/country.entity");
const state_entity_1 = require("../../../../service-lib/src/lib/entities/state.entity");
const city_entity_1 = require("../../../../service-lib/src/lib/entities/city.entity");
const response_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/response.utils");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const enum_1 = require("../../../../../../libs/service-lib/src/lib/enum");
const constants_1 = require("../../../../../../libs/service-lib/src/lib/constants");
let AddressRepository = class AddressRepository {
    constructor(addressRepository, dataSource) {
        this.addressRepository = addressRepository;
        this.dataSource = dataSource;
    }
    /** Creates a new address in the database. */
    async createAddress(addressData) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        await queryRunner.startTransaction();
        try {
            const address = this.addressRepository.create(addressData);
            const savedAddress = await queryRunner.manager.save(address_entity_1.Address, address);
            await queryRunner.commitTransaction();
            return savedAddress;
        }
        catch (error) {
            await queryRunner.rollbackTransaction();
            throw new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.internalServerError, messages_1.errorMessages.addressQueryCreationFailed));
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Fetches paginated address details with optional search functionality. */
    async fetchAllAddressDetails(page, limit, search, sort) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        await queryRunner.startTransaction();
        try {
            const skip = (page - 1) * limit;
            // Build search condition if search term is provided
            const whereCondition = search
                ? [
                    { address1: (0, typeorm_2.ILike)(`%${search}%`) },
                    { area: (0, typeorm_2.ILike)(`%${search}%`) },
                    { pinCode: (0, typeorm_2.ILike)(`%${search}%`) },
                ]
                : {};
            // Build dynamic sorting condition
            let sortType = {};
            if (sort) {
                sort.split(",").forEach((sortParam) => {
                    const [field, order] = sortParam.split(":");
                    if (field &&
                        (order === constants_1.DEFAULT_ASC_SORT_ORDER ||
                            order === constants_1.DEFAULT_DESC_SORT_ORDER)) {
                        sortType[field] = order;
                    }
                });
            }
            else {
                sortType = { [constants_1.DEFAULT_SORT_FIELD]: constants_1.DEFAULT_SORT_ORDER };
            }
            // Fetch data along with total count
            const [addresses, count] = await queryRunner.manager.findAndCount(address_entity_1.Address, {
                skip,
                take: limit,
                where: whereCondition,
                relations: ["countryId", "stateId", "cityId"],
                order: sortType,
            });
            await queryRunner.commitTransaction();
            return {
                data: addresses,
                count,
            };
        }
        catch (error) {
            console.error("Error fetching paginated addresses:", error);
            await queryRunner.rollbackTransaction();
            throw new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.internalServerError, messages_1.errorMessages.addressQueryFetchFailed));
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves the details of a specific address by its ID. */
    async getAddressDetails(id) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        await queryRunner.startTransaction();
        try {
            const address = await queryRunner.manager.findOne(address_entity_1.Address, {
                where: { id },
                relations: ["countryId", "stateId", "cityId"],
            });
            if (!address) {
                throw new common_1.NotFoundException(`Address with ID ${id} not found`);
            }
            await queryRunner.commitTransaction();
            return address;
        }
        catch (error) {
            console.error("Error fetching address details:", error);
            await queryRunner.rollbackTransaction();
            throw error instanceof common_1.NotFoundException
                ? error
                : new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.internalServerError, messages_1.errorMessages.addressQueryFetchFailed));
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Updates the details of an existing address. */
    async updateAddressDetails(addressId, updateAddress) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        await queryRunner.startTransaction();
        try {
            // Ensure the entity exists in the database
            const existingAddress = await queryRunner.manager.findOne(address_entity_1.Address, {
                where: { id: addressId },
            });
            if (!existingAddress) {
                throw new common_1.NotFoundException(`Address with ID ${addressId} not found`);
            }
            // Fetch related entities only if they exist in the payload
            const country = updateAddress.countryId
                ? await queryRunner.manager.findOne(country_entity_1.Country, {
                    where: { id: updateAddress.countryId },
                })
                : existingAddress.countryId;
            const state = updateAddress.stateId
                ? await queryRunner.manager.findOne(state_entity_1.State, {
                    where: { id: updateAddress.stateId },
                })
                : existingAddress.stateId;
            const city = updateAddress.cityId
                ? await queryRunner.manager.findOne(city_entity_1.City, {
                    where: { id: updateAddress.cityId },
                })
                : existingAddress.cityId;
            // Merge existing address with updated data
            const updatedData = {
                ...existingAddress, // Retain existing data
                ...updateAddress, // Apply new updates
                countryId: country, // Use fetched or existing reference
                stateId: state,
                cityId: city,
            };
            // Save the updated entity
            const updatedAddress = await queryRunner.manager.save(address_entity_1.Address, updatedData);
            await queryRunner.commitTransaction();
            return updatedAddress;
        }
        catch (error) {
            await queryRunner.rollbackTransaction();
            throw error instanceof common_1.NotFoundException
                ? error
                : new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.internalServerError, messages_1.errorMessages.addressQueryUpdationFailed));
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Removes an address from the database. */
    async removeAddressDetails(address) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        await queryRunner.startTransaction();
        try {
            const addressData = await this.getAddressDetails(address.id);
            await queryRunner.manager.remove(addressData);
            await queryRunner.commitTransaction();
        }
        catch (error) {
            await queryRunner.rollbackTransaction();
            throw new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.internalServerError, messages_1.errorMessages.addressQueryDeletionFailed));
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves all the region details */
    async findAllRegions() {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        try {
            return await queryRunner.manager.find(region_entity_1.Region);
        }
        catch (error) {
            throw new Error(messages_1.errorMessages.regionQueryFetchFailed);
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves the countries details by region id */
    async findCountries(regionId) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        try {
            const query = queryRunner.manager.createQueryBuilder(country_entity_1.Country, "country");
            if (regionId) {
                query.where("country.region_id = :regionId", { regionId });
            }
            return await query.getMany();
        }
        catch (error) {
            throw new Error(messages_1.errorMessages.countryQueryFetchFailed);
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves all the countries details */
    async fetchAllCountries() {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        try {
            return await queryRunner.manager.find(country_entity_1.Country);
        }
        catch (error) {
            throw new Error(messages_1.errorMessages.countryQueryFetchFailed);
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves the state details by country id */
    async findStates(countryId) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        try {
            const query = queryRunner.manager.createQueryBuilder(state_entity_1.State, "state");
            if (countryId) {
                query.where("state.country_id = :countryId", { countryId });
            }
            return await query.getMany();
        }
        catch (error) {
            throw new Error(messages_1.errorMessages.stateQueryFetchFailed);
        }
        finally {
            await queryRunner.release();
        }
    }
    /** Retrieves the city details by state id */
    async findCities(stateId) {
        const queryRunner = this.dataSource.createQueryRunner();
        await queryRunner.connect();
        try {
            const query = queryRunner.manager.createQueryBuilder(city_entity_1.City, "city");
            if (stateId) {
                query.where("city.state_id = :stateId", { stateId });
            }
            return await query.getMany();
        }
        catch (error) {
            throw new Error(messages_1.errorMessages.cityQueryFetchFailed);
        }
        finally {
            await queryRunner.release();
        }
    }
};
exports.AddressRepository = AddressRepository;
exports.AddressRepository = AddressRepository = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(address_entity_1.Address)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.DataSource !== "undefined" && typeorm_2.DataSource) === "function" ? _b : Object])
], AddressRepository);
